import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthViewModel } from '../ViewModel/AuthViewModel';
import { Github, Cloud, GitBranch, Shield } from 'lucide-react';
import MFAVerificationPopup from '../Components/MFAVerificationPopup';
import { MFAService } from '../Service/MFAService';

export const LoginView: React.FC = () => {
  const { login, isLoading } = useAuthViewModel();
  const navigate = useNavigate();
  const [showMFAPopup, setShowMFAPopup] = useState(false);
  const [mfaData, setMFAData] = useState<{
    email: string;
    qai: string;
    code: string;
    provider: string;
  } | null>(null);

  const handleLogin = async (provider: 'github' | 'gitlab' | 'bitbucket') => {
    try {
      const result = await login(provider);
      if (result && result.requiresMFA) {
        setMFAData({
          email: result.email,
          qai: result.qai,
          code: result.code,
          provider,
        });
        setShowMFAPopup(true);
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleMFAVerification = async (token: string) => {
    if (!mfaData) return;
    try {
      await MFAService.verifyMFA(
        mfaData.email,
        token,
        mfaData.code,
        'user_type',
        mfaData.provider
      );
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="flex h-screen bg-[#09090e] overflow-hidden">

      {/* ── Left panel — decorative ── */}
      <div className="hidden lg:flex lg:flex-1 flex-col items-center justify-center relative p-12 overflow-hidden">
        {/* Ambient glows */}
        <div
          className="absolute top-1/4 left-1/3 w-80 h-80 rounded-full opacity-20 blur-[100px] pointer-events-none"
          style={{ background: 'radial-gradient(circle,#6366f1,transparent)' }}
        />
        <div
          className="absolute bottom-1/3 right-1/4 w-64 h-64 rounded-full opacity-10 blur-[80px] pointer-events-none"
          style={{ background: 'radial-gradient(circle,#a855f7,transparent)' }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(#818cf8 1px,transparent 1px),linear-gradient(90deg,#818cf8 1px,transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 max-w-md text-center">
          {/* Pseudo-terminal */}
          <div className="bg-[#111116] border border-[#26262f] rounded-[12px] p-5 mb-8 text-left">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-[#f43f5e]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#eab308]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
              <span className="text-[11px] text-[#52525f] ml-2 font-mono">qixdeploy deploy</span>
            </div>
            <div className="space-y-1.5 font-mono text-[12px]">
              <div className="text-[#52525f]">→  Analysing repository structure…</div>
              <div className="text-[#818cf8]">✓  Detected Node.js + PostgreSQL</div>
              <div className="text-[#52525f]">→  Generating optimised Dockerfile…</div>
              <div className="text-[#818cf8]">✓  Multi-stage build configured</div>
              <div className="text-[#52525f]">→  Deploying to your AWS account…</div>
              <div className="text-[#22c55e]">🎉 Deployment successful!</div>
              <div className="text-[#52525f] mt-2">
                {'   '}App live at:{' '}
                <span className="text-[#818cf8]">https://myapp.aws.com</span>
              </div>
            </div>
          </div>

          <h1
            className="text-[32px] font-bold text-[#eeeef5] mb-3 leading-tight"
            style={{ fontFamily: "'Syne',sans-serif" }}
          >
            Deploy smarter.
            <br />
            <span className="text-[#818cf8]">Your cloud, your rules.</span>
          </h1>
          <p className="text-[14px] text-[#8e8ea8] leading-relaxed">
            AI-powered deployments into your own AWS, GCP, or Azure account. No DevOps expertise required.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
            {['BYOC', 'AI Dockerfile', 'CI/CD', 'Multi-cloud'].map((f) => (
              <span
                key={f}
                className="text-[11px] font-semibold bg-[rgba(99,102,241,0.1)] text-[#818cf8] border border-[rgba(99,102,241,0.2)] px-2.5 py-1 rounded-full"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — sign-in form ── */}
      <div className="w-full lg:w-[420px] flex items-center justify-center px-6 bg-[#0d0d12] lg:border-l lg:border-[#26262f]">
        <div className="w-full max-w-[360px]">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div
              className="w-12 h-12 rounded-[12px] flex items-center justify-center text-white font-black text-[15px] mb-4 shadow-lg"
              style={{
                background: 'linear-gradient(135deg,#6366f1,#a855f7)',
                fontFamily: "'Syne',sans-serif",
              }}
            >
              Qx
            </div>
            <h2
              className="text-[24px] font-bold text-[#eeeef5]"
              style={{ fontFamily: "'Syne',sans-serif" }}
            >
              Sign in
            </h2>
            <p className="text-[13px] text-[#52525f] mt-1.5 text-center">
              Access your dashboard seamlessly
            </p>
          </div>

          {/* Provider buttons */}
          <div className="space-y-3">
            <button
              onClick={() => handleLogin('github')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2.5 bg-[#1a1a1a] border border-[#333] text-white rounded-[8px] py-3 px-4 text-[13.5px] font-semibold hover:bg-[#252525] hover:border-[#444] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Github size={17} />
              {isLoading ? 'Signing in…' : 'Continue with GitHub'}
            </button>


            {/* GitLab — commented out same as before, uncomment when ready */}
            {/* <button
              onClick={() => handleLogin('gitlab')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2.5 bg-[rgba(252,109,38,0.1)] border border-[rgba(252,109,38,0.3)] text-[#fc6d26] rounded-[8px] py-3 px-4 text-[13.5px] font-semibold hover:bg-[rgba(252,109,38,0.18)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <GitBranch size={17} />
              {isLoading ? 'Signing in…' : 'Continue with GitLab'}
            </button> */}

            {/* <button
              onClick={() => handleLogin('bitbucket')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2.5 bg-[rgba(38,132,255,0.1)] border border-[rgba(38,132,255,0.3)] text-[#2684ff] rounded-[8px] py-3 px-4 text-[13.5px] font-semibold hover:bg-[rgba(38,132,255,0.18)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Cloud size={17} />
              {isLoading ? 'Signing in…' : 'Continue with Bitbucket'}
            </button> */}
          </div>

          <div className="mt-6 flex items-center gap-2 text-[11.5px] text-[#52525f] justify-center">
            <Shield size={12} />
            <span>Secured with enterprise-grade encryption · SOC 2 Type II</span>
          </div>
        </div>
      </div>

      {/* MFA popup — unchanged */}
      {showMFAPopup && mfaData && (
        <MFAVerificationPopup
          qrCode={mfaData.qai}
          email={mfaData.email}
          onSubmit={handleMFAVerification}
          onCancel={() => {
            setShowMFAPopup(false);
            setMFAData(null);
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
